import React from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { useI18n } from "../i18n";

interface LazyErrorBoundaryProps {
  children: React.ReactNode;
  fallbackClassName?: string;
}

interface LazyErrorBoundaryState {
  hasError: boolean;
}

/**
 * LazyErrorBoundary — compact inline error boundary for lazy-loaded chunks.
 * On chunk load failure, shows a small message with a retry button.
 * Retry = remount children → re-trigger React.lazy dynamic import.
 */
export default class LazyErrorBoundary extends React.Component<
  LazyErrorBoundaryProps,
  LazyErrorBoundaryState
> {
  private retryButtonRef = React.createRef<HTMLButtonElement>();

  constructor(props: LazyErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): LazyErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(_error: Error, _errorInfo: React.ErrorInfo): void {
    // Chunk load errors are expected when network is unavailable.
    // No additional logging needed — React logs the error boundary itself.
  }

  componentDidUpdate(
    _prevProps: LazyErrorBoundaryProps,
    prevState: LazyErrorBoundaryState,
  ): void {
    if (this.state.hasError && !prevState.hasError) {
      this.retryButtonRef.current?.focus();
    }
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <LazyErrorFallback
          fallbackClassName={this.props.fallbackClassName}
          retryButtonRef={this.retryButtonRef}
          onRetry={this.handleRetry}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Functional component for the error fallback UI.
 * Extracted so we can use the useI18n hook (hooks are not available in class components).
 */
function LazyErrorFallback({
  fallbackClassName,
  retryButtonRef,
  onRetry,
}: {
  fallbackClassName?: string;
  retryButtonRef: React.RefObject<HTMLButtonElement | null>;
  onRetry: () => void;
}): React.ReactElement {
  const { t } = useI18n();

  return (
    <div
      role="alert"
      className={`flex flex-col items-center justify-center gap-3 p-4 min-h-[120px] ${fallbackClassName ?? ""}`}
    >
      <AlertTriangle
        className="text-muted-foreground"
        size={24}
        aria-hidden
      />
      <p className="text-ui text-muted-foreground text-center">
        {t("error.chunkLoadFailed")}
      </p>
      <Button
        ref={retryButtonRef}
        variant="outline"
        size="sm"
        onClick={onRetry}
      >
        {t("common.retry")}
      </Button>
    </div>
  );
}
